/**
 * 运输单货物明细的最小结构；避免 utils 反向依赖 entity 包。
 */
export interface TransportGoodsInfoLike {
    goodsName?: string;
    model?: string;
    unit?: string;
    ton?: number | string;
    amount?: number | string;
    deliveryQuantity?: number | string;
    transportWeight?: number | string;
    unloadedWeight?: number | string;
    transportVolume?: number | string;
    transportItems?: number | string;
    unitPrice?: number | string;
    totalPrice?: number | string;
    receiptQuantity?: number | string;
}

export interface TransportGoodsSourceLike {
    goodsDetails?: TransportGoodsInfoLike[];
    goodsName?: string;
    model?: string;
    unit?: string;
    unitPrice?: number | string;
    totalPrice?: number | string;
}

const hasDisplayValue = (value: unknown) => value !== undefined && value !== null && value !== "";

const toFiniteNumber = (value: unknown) => {
    if (!hasDisplayValue(value)) return undefined;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : undefined;
};

const getSourceValue = (source: TransportGoodsSourceLike, prop: string) => {
    const sourceRecord = source as Record<string, unknown>;
    return sourceRecord[prop] as number | string | undefined;
};

const getDetailValue = (item: TransportGoodsInfoLike, prop: string) => {
    const itemRecord = item as Record<string, unknown>;
    return itemRecord[prop] as number | string | undefined;
};

const getQuantityValue = (item: TransportGoodsInfoLike, quantityProp: string) => {
    return (
        getDetailValue(item, quantityProp) ??
        item.transportWeight ??
        item.ton ??
        item.amount ??
        item.deliveryQuantity ??
        item.receiptQuantity
    );
};

const hasGoodsInfoContent = (item: TransportGoodsInfoLike) => {
    return Boolean(
        item.goodsName ||
        item.model ||
        hasDisplayValue(item.ton) ||
        hasDisplayValue(item.amount) ||
        hasDisplayValue(item.deliveryQuantity) ||
        hasDisplayValue(item.transportWeight) ||
        hasDisplayValue(item.unloadedWeight) ||
        hasDisplayValue(item.transportVolume) ||
        hasDisplayValue(item.transportItems) ||
        hasDisplayValue(item.unitPrice) ||
        hasDisplayValue(item.totalPrice) ||
        hasDisplayValue(item.receiptQuantity)
    );
};

/**
 * 运单新接口优先读取 goodsDetails，旧接口没有数组时用单货物字段兜底成一条明细。
 */
export const normalizeTransportGoodsInfoList = (
    source: TransportGoodsSourceLike | undefined,
    quantityProp: string
): TransportGoodsInfoLike[] => {
    if (!source) return [];
    const details = Array.isArray(source.goodsDetails) ? source.goodsDetails.filter(hasGoodsInfoContent) : [];
    if (details.length) return details;

    const fallbackItem: TransportGoodsInfoLike = {
        goodsName: source.goodsName,
        model: source.model,
        unit: source.unit,
        amount:
            getSourceValue(source, quantityProp) ??
            getSourceValue(source, "transportWeight") ??
            getSourceValue(source, "ton") ??
            getSourceValue(source, "amount") ??
            getSourceValue(source, "deliveryQuantity"),
        unloadedWeight: getSourceValue(source, "unloadedWeight"),
        transportVolume: getSourceValue(source, "transportVolume"),
        transportItems: getSourceValue(source, "transportItems"),
        unitPrice: source.unitPrice,
        totalPrice: source.totalPrice
    };
    return hasGoodsInfoContent(fallbackItem) ? [fallbackItem] : [];
};

/**
 * 多货物文本按 PRD 要求使用顿号拼接，保持列表和详情的展示口径一致。
 */
export const joinTransportGoodsField = (
    source: TransportGoodsSourceLike | undefined,
    field: keyof TransportGoodsInfoLike,
    quantityProp = "amount"
) => {
    return normalizeTransportGoodsInfoList(source, quantityProp)
        .map(item => item[field])
        .filter(hasDisplayValue)
        .map(item => String(item))
        .join("、");
};

/**
 * 运输数量优先按指定字段汇总，其次兼容 goodsDetails.transportWeight 和旧 GoodsInfo 数量字段。
 */
export const sumTransportGoodsQuantity = (source: TransportGoodsSourceLike | undefined, quantityProp: string) => {
    const list = normalizeTransportGoodsInfoList(source, quantityProp);
    let hasQuantity = false;
    const total = list.reduce((sum, item) => {
        const value = toFiniteNumber(getQuantityValue(item, quantityProp));
        if (value === undefined) return sum;
        hasQuantity = true;
        return sum + value;
    }, 0);
    return hasQuantity ? total : undefined;
};

/**
 * 任意货物行有单价时，金额列和合计金额才需要展示。
 */
export const hasTransportGoodsUnitPrice = (source: TransportGoodsSourceLike | undefined, quantityProp = "amount") => {
    return normalizeTransportGoodsInfoList(source, quantityProp).some(item => hasDisplayValue(item.unitPrice));
};

/**
 * 货物金额合计；优先汇总后端 totalPrice，缺失时用数量 * 单价兜底。
 */
export const sumTransportGoodsTotalPrice = (source: TransportGoodsSourceLike | undefined, quantityProp: string) => {
    const list = normalizeTransportGoodsInfoList(source, quantityProp);
    let hasPrice = false;
    const total = list.reduce((sum, item) => {
        const totalPrice = toFiniteNumber(item.totalPrice);
        if (totalPrice !== undefined) {
            hasPrice = true;
            return sum + totalPrice;
        }

        const unitPrice = toFiniteNumber(item.unitPrice);
        const quantity = toFiniteNumber(getQuantityValue(item, quantityProp));
        if (unitPrice === undefined || quantity === undefined) return sum;
        hasPrice = true;
        return sum + unitPrice * quantity;
    }, 0);
    return hasPrice ? total : undefined;
};
